import { useMemo } from "react";
import { BoardNote } from "@/bryntum-lib/models/BoardNote";
import { NotesEditor } from "@/react/components/entityViews/notes/components/editors/NotesEditor";
import { useSwivuNotes } from "./useSwivuNotes";
import { EntityRecord } from "@/types/entity";
import { useEntityNotesContextOptional } from "@/react/components/entityViews/notes/context/EntityNotesContext";

interface UseNotesMenuProps {
  record: EntityRecord;
  noteRecord: BoardNote | null;
  isEditor?: boolean;
  onDeleteNote?: () => void;
  onOpenNewNote?: () => void;
}

export const useNotesMenu = ({ record, noteRecord, isEditor = false, onDeleteNote, onOpenNewNote }: UseNotesMenuProps) => {
  const context = useEntityNotesContextOptional();
  const { newNote: localNewNote, loading, createNewNote: localCreateNewNote, deleteNote, duplicateNoteRecord, copyNoteLink, canEditRecord } = useSwivuNotes({ entityRecord: record });
  const newNote = context?.newNote || localNewNote; // Use shared newNote from context if available
  const createNewNote = context?.createNewNote || localCreateNewNote; // Use shared createNewNote from context if available
  const canEdit = noteRecord && !noteRecord.isPhantom ? canEditRecord(noteRecord) : true; // Allow creating new notes

  // Memoize the NotesEditor components to prevent recreation on rerenders
  const NewNoteEditor = useMemo(() => {
    return ({
      onFinish,
      isDirty,
      showUnsavedDialog,
      setShowUnsavedDialog,
      onDeleteNote
    }: {
      onFinish?: () => void;
      isDirty?: (isDirty: boolean) => void;
      showUnsavedDialog?: boolean;
      setShowUnsavedDialog?: (showUnsavedDialog: boolean) => void;
      onDeleteNote?: () => void;
    }) => (
      <NotesEditor
        onDeleteNote={onDeleteNote}
        noteRecord={newNote!}
        entityRecord={record}
        onFinish={onFinish}
        isDirty={isDirty}
        showUnsavedDialog={showUnsavedDialog}
        setShowUnsavedDialog={setShowUnsavedDialog}
      />
    );
  }, [newNote, record]);

  const OpenNoteEditor = useMemo(() => {
    return ({
      onFinish,
      isDirty,
      showUnsavedDialog,
      setShowUnsavedDialog,
      onDeleteNote
    }: {
      onFinish?: () => void;
      isDirty?: (isDirty: boolean) => void;
      showUnsavedDialog?: boolean;
      setShowUnsavedDialog?: (showUnsavedDialog: boolean) => void;
      onDeleteNote?: () => void;
    }) => (
      <NotesEditor
        onDeleteNote={onDeleteNote}
        noteRecord={noteRecord!}
        entityRecord={record}
        onFinish={onFinish}
        isDirty={isDirty}
        showUnsavedDialog={showUnsavedDialog}
        setShowUnsavedDialog={setShowUnsavedDialog}
      />
    );
  }, [noteRecord, record]);

  const handleCreateNewNote = () => {
    createNewNote(record);
    onOpenNewNote?.();
  };

  const handleDeleteNote = async () => {
    if (noteRecord) {
      const isDelete = await deleteNote(noteRecord.id);
      if (isDelete) {
        onDeleteNote?.();
      }
    }
  };

  const handleCopyLink = () => {
    if (noteRecord) {
      copyNoteLink(noteRecord, record);
    }
  };

  const handleDuplicateNote = async () => {
    if (noteRecord) {
      await duplicateNoteRecord(noteRecord);
    }
  };

  return {
    newNote,
    loading,
    canEdit,
    NewNoteEditor,
    OpenNoteEditor,
    handleCreateNewNote,
    handleDeleteNote,
    handleCopyLink,
    handleDuplicateNote,
    isEditor
  };
};
